/**
 * OM Analyzer API client — wraps all /api/v1/om-analyzer endpoints.
 */
import { get, post, put, patch, del, postFormData, apiClient } from '@/lib/api-client'
import type {
  AnthropicFile,
  OmDealCloudPushResponse,
  OmDocument,
  OmDocumentFileStatus,
  OmExtractionProgress,
  OmExtractionStartRequest,
  OmExtractionTrace,
  OmField,
  OmFieldCreate,
  OmFieldReorderItem,
  OmFieldUpdate,
  OmInstructionField,
  OmInstructionFieldCreate,
  OmInstructionFieldUpdate,
  OmInstructionSet,
  OmInstructionSetCreate,
  OmLoadInstructionSetRequest,
  OmNewSessionForDocumentResponse,
  OmResult,
  OmResultPatch,
  OmSession,
  OmSessionCreate,
  OmSessionUpdate,
  OmSessionEvidence,
  OmUploadAndScanResponse,
  OmUploadFastResponse,
} from '@/types/om-analyzer'

const BASE = '/om-analyzer'

// ── Instruction Sets ──────────────────────────────────────────────────────────

export const omInstructionSetsApi = {
  list: ()                             => get<OmInstructionSet[]>(`${BASE}/instruction-sets`),
  get:  (id: string)                   => get<OmInstructionSet>(`${BASE}/instruction-sets/${id}`),
  getDefault: ()                       => get<OmInstructionSet | null>(`${BASE}/instruction-sets/default`),
  create: (payload: OmInstructionSetCreate) =>
    post<OmInstructionSet>(`${BASE}/instruction-sets`, payload),
  update: (id: string, payload: { name?: string; description?: string | null; is_default?: boolean }) =>
    put<OmInstructionSet>(`${BASE}/instruction-sets/${id}`, payload),
  delete: (id: string) => del<void>(`${BASE}/instruction-sets/${id}`),
  setDefault:   (id: string) => post<OmInstructionSet>(`${BASE}/instruction-sets/${id}/set-default`, {}),
  unsetDefault: (id: string) => del<OmInstructionSet>(`${BASE}/instruction-sets/${id}/unset-default`),

  // Fields within a set
  addField: (setId: string, payload: OmInstructionFieldCreate) =>
    post<OmInstructionField>(`${BASE}/instruction-sets/${setId}/fields`, payload),
  updateField: (setId: string, fieldId: string, payload: OmInstructionFieldUpdate) =>
    put<OmInstructionField>(`${BASE}/instruction-sets/${setId}/fields/${fieldId}`, payload),
  deleteField: (setId: string, fieldId: string) =>
    del<void>(`${BASE}/instruction-sets/${setId}/fields/${fieldId}`),
}

// ── Upload-and-scan ───────────────────────────────────────────────────────────

export interface ModelOverridePayload {
  provider_key: string
  model_id:     string
}

export const omUploadAndScanApi = {
  upload: (file: File, useClaude = true, modelOverride?: ModelOverridePayload | null, dealFlowType?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('use_claude_file', String(useClaude))
    if (modelOverride) {
      fd.append('model_override_provider', modelOverride.provider_key)
      fd.append('model_override_model_id', modelOverride.model_id)
    }
    if (dealFlowType) {
      fd.append('deal_flow_type', dealFlowType)
    }
    return postFormData<OmUploadAndScanResponse>(`${BASE}/upload-and-scan`, fd)
  },
}

export const omUploadFastApi = {
  upload: (file: File, useClaude = true, modelOverride?: ModelOverridePayload | null) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('use_claude_file', String(useClaude))
    if (modelOverride) {
      fd.append('model_override_provider', modelOverride.provider_key)
      fd.append('model_override_model_id', modelOverride.model_id)
    }
    return postFormData<OmUploadFastResponse>(`${BASE}/upload-and-scan`, fd)
  },
}

export const omNewSessionApi = {
  createForDocument: (payload: { document_id: string; deal_name: string; notes: string }) =>
    post<OmNewSessionForDocumentResponse>(`${BASE}/new-session-for-document`, payload),
}

// ── Sessions ──────────────────────────────────────────────────────────────────

export const omSessionsApi = {
  list: (includeDeleted = false) =>
    get<OmSession[]>(`${BASE}/sessions`, { include_deleted: includeDeleted }),
  get:     (id: string) => get<OmSession>(`${BASE}/sessions/${id}`),
  create:  (payload: OmSessionCreate) => post<OmSession>(`${BASE}/sessions`, payload),
  update:  (id: string, payload: OmSessionUpdate) => put<OmSession>(`${BASE}/sessions/${id}`, payload),
  delete:  (id: string) => del<void>(`${BASE}/sessions/${id}`),
  restore: (id: string) => post<OmSession>(`${BASE}/sessions/${id}/restore`),
}

// ── Documents ─────────────────────────────────────────────────────────────────

export const omDocumentsApi = {
  list:   (sessionId: string) => get<OmDocument[]>(`${BASE}/sessions/${sessionId}/documents`),
  upload: (sessionId: string, file: File, useClaude = true, modelOverride?: ModelOverridePayload | null) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('use_claude_file', String(useClaude))
    if (modelOverride) {
      fd.append('model_override_provider', modelOverride.provider_key)
      fd.append('model_override_model_id', modelOverride.model_id)
    }
    return postFormData<OmDocument>(`${BASE}/sessions/${sessionId}/documents`, fd)
  },
  delete: (sessionId: string, docId: string) =>
    del<void>(`${BASE}/sessions/${sessionId}/documents/${docId}`),
  getFile: async (sessionId: string, docId: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      `${BASE}/sessions/${sessionId}/documents/${docId}/file`,
      { responseType: 'blob' },
    )
    return response.data
  },
  fileStatus: (sessionId: string, docId: string) =>
    get<OmDocumentFileStatus>(`${BASE}/sessions/${sessionId}/documents/${docId}/file-status`),
}

// ── Anthropic Files (admin) ───────────────────────────────────────────────────

export const anthropicFilesApi = {
  list: (params?: { skip?: number; limit?: number; status?: 'active' | 'deleted' }) =>
    get<AnthropicFile[]>('/admin/anthropic-files', params),
  delete: (recordId: string) => del<void>(`/admin/anthropic-files/${recordId}`),
}

// ── Fields ────────────────────────────────────────────────────────────────────

export const omFieldsApi = {
  list:   (sessionId: string) => get<OmField[]>(`${BASE}/sessions/${sessionId}/fields`),
  add:    (sessionId: string, payload: OmFieldCreate) =>
    post<OmField>(`${BASE}/sessions/${sessionId}/fields`, payload),
  update: (sessionId: string, fieldId: string, payload: OmFieldUpdate) =>
    put<OmField>(`${BASE}/sessions/${sessionId}/fields/${fieldId}`, payload),
  delete: (sessionId: string, fieldId: string) =>
    del<void>(`${BASE}/sessions/${sessionId}/fields/${fieldId}`),
  reorder: (sessionId: string, items: OmFieldReorderItem[]) =>
    post<OmField[]>(`${BASE}/sessions/${sessionId}/fields/reorder`, items),
  loadInstructionSet: (sessionId: string, req: OmLoadInstructionSetRequest) =>
    post<OmField[]>(`${BASE}/sessions/${sessionId}/fields/load-instruction-set`, req),
}

// ── Results ───────────────────────────────────────────────────────────────────

export const omResultsApi = {
  list:  (sessionId: string, documentId?: string) =>
    get<OmResult[]>(`${BASE}/sessions/${sessionId}/results`, documentId ? { document_id: documentId } : undefined),
  patch: (sessionId: string, resultId: string, payload: OmResultPatch) =>
    patch<OmResult>(`${BASE}/sessions/${sessionId}/results/${resultId}`, payload),
}

// ── Job detail (widget) ───────────────────────────────────────────────────────

export interface OmJobDetailFieldItem {
  field_id: string
  field_name: string
  document_name: string | null
  status: string
}

export interface OmJobFailureEntry {
  status: string
  error_type?: string
  error_message?: string
  attempts?: number
  dismissed_by_user?: boolean
  dismissed_at?: string | null
  failed_at?: string | null
  // Field extraction specific
  failed_field_ids?: string[]
  failed_field_labels?: string[]
}

export interface OmJobDetail {
  session_id: string
  deal_id: string | null
  status: string
  stage: string | null
  progress_pct: number
  total_fields: number
  completed: number
  errors: number
  outstanding: number
  current_field: string | null
  fields: OmJobDetailFieldItem[]
  analyzer_status: string | null
  push_status: string | null
  // DealCloud REST push outcome (migration 206) — independent of push_status;
  // 'pending' | 'running' | 'complete' | 'failed' written by
  // _push_to_dealcloud in om_push_service.
  dealcloud_push_status: string | null
  tagging_status: string | null
  entity_status: string | null
  image_status: string | null
  intelligence_status: string | null
  rialto360_status: string | null
  triage_status: string | null
  indexing_delay: boolean
  // Failure tracking (migration 145)
  field_total: number
  field_success: number
  field_failed: number
  partial_failure: boolean
  failure_summary: Record<string, OmJobFailureEntry> | null
}

// ── Extraction ────────────────────────────────────────────────────────────────

export const omExtractionApi = {
  start:     (sessionId: string, payload?: OmExtractionStartRequest) =>
    post<OmExtractionProgress>(`${BASE}/sessions/${sessionId}/extract`, payload ?? {}),
  stop:      (sessionId: string) => post<{ message: string }>(`${BASE}/sessions/${sessionId}/stop`),
  progress:  (sessionId: string) => get<OmExtractionProgress>(`${BASE}/sessions/${sessionId}/progress`),
  jobDetail: (sessionId: string) => get<OmJobDetail>(`${BASE}/sessions/${sessionId}/job-detail`),
}

// ── Export ────────────────────────────────────────────────────────────────────

export function exportResults(sessionId: string, format: 'xlsx' | 'csv'): void {
  // apiClient.defaults.baseURL is already /api/v1 — BASE starts with /om-analyzer
  const baseURL = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '')
  const url = `${baseURL}${BASE}/sessions/${sessionId}/export?format=${format}`
  window.open(url, '_blank')
}

// ── DealCloud push ────────────────────────────────────────────────────────────

export interface TriggerDealCloudPushResponse {
  status:     string
  session_id: string
  message:    string
}

/** Body of the 409 the backend returns when the deal's stored dealcloud_id
 *  no longer resolves to a row in DealCloud (see trigger_dealcloud_push). */
export interface DealCloudEntryMissingDetail {
  status:       'not_found_in_dealcloud'
  session_id:   string
  dealcloud_id: string
  message:      string
}

export const omDealCloudApi = {
  push: (sessionId: string) =>
    post<OmDealCloudPushResponse>(`${BASE}/sessions/${sessionId}/push-to-dealcloud`),
  triggerPush: (sessionId: string, forceCreate = false) =>
    post<TriggerDealCloudPushResponse>(
      `${BASE}/sessions/${sessionId}/trigger-dealcloud-push${forceCreate ? '?force_create=true' : ''}`
    ),
}

// ── Extraction trace ──────────────────────────────────────────────────────────

export const omTraceApi = {
  get: (sessionId: string, resultId: string) =>
    get<OmExtractionTrace>(`${BASE}/sessions/${sessionId}/results/${resultId}/trace`),
}

// ── Evidence ──────────────────────────────────────────────────────────────────

export const omEvidenceApi = {
  list: (sessionId: string) =>
    get<OmSessionEvidence>(`${BASE}/sessions/${sessionId}/evidence`),
  /** Full URL for use with fetch() + Authorization header. */
  documentFileUrl: (sessionId: string, documentId: string): string => {
    const base = (import.meta.env.VITE_API_BASE_URL ?? '') as string
    return `${base}/api/v1${BASE}/sessions/${sessionId}/documents/${documentId}/file`
  },
}
